import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Bell, BellOff, ExternalLink, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/context/ToastContext";
import { formatNextDelivery } from "@/lib/scheduleTimes";
import { api, ApiError } from "@/services/api";

import type {
  KaiScheduledReportRow,
  KaiScheduledReportsSnapshot,
} from "./kaiScheduledReportsTypes";

/**
 * Renders a deterministic `scheduled_reports_snapshot` artifact inline in Kai
 * chat: a scannable list of the scheduled email reports the user can see, with
 * inline Subscribe / Unsubscribe and (on the Bookings page, for custom booking
 * reports) a "View on bookings" action.
 */
export function KaiScheduledReportsSnapshot({
  spec,
  beforeNavigate,
}: {
  spec: KaiScheduledReportsSnapshot;
  beforeNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Local subscription overrides so inline toggles reflect immediately.
  const [subscribed, setSubscribed] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(spec.rows.map((r) => [r.id, r.subscribed])),
  );
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);

  const title = spec.title?.trim() || "Scheduled reports";
  const onBookings = spec.context === "bookings";

  async function handleToggle(row: KaiScheduledReportRow) {
    const next = !(subscribed[row.id] ?? row.subscribed);
    setTogglingId(row.id);
    try {
      await api.toggleReportSubscription(row.id, next);
      setSubscribed((prev) => ({ ...prev, [row.id]: next }));
      toast({
        title: next ? "Subscribed" : "Unsubscribed",
        description: next
          ? `You'll receive "${row.name}" by email.`
          : `You'll no longer receive "${row.name}".`,
      });
    } catch (err) {
      toast({
        title: "Could not update subscription",
        description:
          err instanceof ApiError
            ? err.message
            : "Something went wrong. Please try again.",
      });
    } finally {
      setTogglingId(null);
    }
  }

  async function handleViewOnBookings(row: KaiScheduledReportRow) {
    setOpeningId(row.id);
    try {
      const { path } = await api.getReportScheduleBookingsViewUrl(row.id);
      beforeNavigate?.();
      navigate(path);
    } catch (err) {
      toast({
        title: "Could not open on bookings",
        description:
          err instanceof ApiError
            ? err.message
            : "Something went wrong. Please try again.",
      });
    } finally {
      setOpeningId(null);
    }
  }

  return (
    <div className="glass-panel my-2 flex min-w-0 flex-col gap-3 overflow-hidden rounded-xl p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-base font-semibold text-foreground">
          {title}
        </p>
        <Badge variant="info" className="shrink-0 px-2 py-0.5 text-[10px]">
          {spec.rows.length} {spec.rows.length === 1 ? "report" : "reports"}
        </Badge>
      </div>

      {spec.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No scheduled reports are available to you right now.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border/50">
          {spec.rows.map((row) => {
            const isSubscribed = subscribed[row.id] ?? row.subscribed;
            const nextDelivery = isSubscribed
              ? formatNextDelivery(row.next_delivery_utc)
              : "";
            return (
              <li
                key={row.id}
                className="flex flex-wrap items-start gap-x-3 gap-y-2 py-2.5 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="truncate text-sm font-medium text-foreground">
                      {row.name}
                    </p>
                    {row.is_mine && (
                      <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                        Yours
                      </Badge>
                    )}
                    {row.paused && (
                      <Badge
                        variant="outline"
                        className="px-1.5 py-0 text-[10px] text-muted-foreground"
                      >
                        Paused
                      </Badge>
                    )}
                    {isSubscribed && !row.paused && (
                      <Badge
                        variant="success"
                        className="px-1.5 py-0 text-[10px]"
                      >
                        Subscribed
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {row.report_type_display}
                    {row.schedule_summary ? ` · ${row.schedule_summary}` : ""}
                  </p>
                  {nextDelivery && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Next: {nextDelivery}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {onBookings && row.can_view_on_bookings && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      disabled={openingId === row.id}
                      onClick={() => handleViewOnBookings(row)}
                    >
                      {openingId === row.id ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <ExternalLink className="size-3.5" />
                      )}
                      <span className="ml-1">Bookings</span>
                    </Button>
                  )}
                  {row.can_subscribe && (
                    <Button
                      variant={isSubscribed ? "outline" : "default"}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      disabled={togglingId === row.id}
                      onClick={() => handleToggle(row)}
                    >
                      {togglingId === row.id ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : isSubscribed ? (
                        <BellOff className="size-3.5" />
                      ) : (
                        <Bell className="size-3.5" />
                      )}
                      <span className="ml-1">
                        {isSubscribed ? "Unsubscribe" : "Subscribe"}
                      </span>
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {spec.footnotes && spec.footnotes.length > 0 && (
        <ul className="space-y-1 text-xs text-muted-foreground">
          {spec.footnotes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}

      <div className="flex justify-end border-t border-border/50 pt-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          onClick={() => {
            beforeNavigate?.();
            navigate(spec.open_in_reports_url);
          }}
        >
          Manage in Reports
          <ArrowUpRight className="size-3.5" />
        </button>
      </div>
    </div>
  );
}
